/**
 * Pick a person or a group from the organisation's directory by typing a
 * name (directory.ts: Graph with the person's token, else the API's). Groups
 * are offered only where the organisation has them (never on Mojo Up AI
 * Cloud); where they cannot be searched the list says so rather than asking
 * for an id. A plain id is taken only when there is no directory at all.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { groupsAvailable, groupsSearchable, hasDirectory, resolveSubjects, searchDirectory, subjectName, subscribeNames, type Subject, type SubjectKind } from '../directory.ts';
import { inputClass } from './ui.tsx';

const KIND_TEXT: Record<SubjectKind, string> = { user: 'Person', group: 'Group' };
// An Entra object id, or a Mojo Up AI Cloud person (usr_ and a uuid).
const isId = (s: string) => /^(usr_)?[0-9a-f-]{36}$/i.test(s.trim());

export function SubjectPicker({ kinds: asked, onPick, placeholder, className }: { kinds: SubjectKind[]; onPick: (s: Subject) => void; placeholder?: string; className?: string }) {
  // Mojo Up AI Cloud has no directory groups: a picker asked for people or groups offers people.
  const groups = groupsAvailable();
  const kindsKey = asked.filter((k) => k === 'user' || groups).join(',');
  const kinds = useMemo(() => (kindsKey ? (kindsKey.split(',') as SubjectKind[]) : (['user'] as SubjectKind[])), [kindsKey]);
  const groupsDropped = asked.includes('group') && !groups;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Subject[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [error, setError] = useState<string>();
  /** The query the results on show answer: an answer with no group, where groups cannot be searched, says so. */
  const [searched, setSearched] = useState('');
  const seq = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number }>();
  const directory = hasDirectory();

  // The list is portalled to the body (a card would clip it) and placed under the input; it
  // follows the input on scroll and resize while open.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = input.current?.getBoundingClientRect();
      if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!directory || query.trim().length < 2) return;
    const mine = ++seq.current;
    const t = setTimeout(() => {
      searchDirectory(query, kinds)
        .then((r) => {
          if (mine !== seq.current) return;
          setResults(r);
          setSearched(query);
          setActive(0);
          setError(undefined);
        })
        .catch((e: Error) => mine === seq.current && setError(e.message));
    }, 250);
    return () => clearTimeout(t);
  }, [query, directory, kinds]);

  // Results belong to a query of two characters or more; a shorter one shows none.
  const shown = query.trim().length >= 2 ? results : [];

  const pick = (s: Subject) => {
    onPick(s);
    setQuery('');
    setResults([]);
    setOpen(false);
  };
  // An id is typed only where there is no directory at all; anywhere else people and groups are found by name.
  const manual = !directory;
  const noGroupSearch = kinds.includes('group') && !groupsSearchable() && searched === query && query.trim().length >= 2;
  const manualSubject = (): Subject => ({ type: kinds.length === 1 ? kinds[0]! : 'user', id: query.trim(), name: query.trim() });
  const hint = directory ? (kinds.length === 1 ? `Type a ${KIND_TEXT[kinds[0]!].toLowerCase()}'s name` : 'Type a name') : `${kinds.map((k) => KIND_TEXT[k]).join(' or ')} object id`;

  return (
    <div className={`relative ${className ?? ''}`}>
      <input
        ref={input}
        role="combobox"
        aria-expanded={open && shown.length > 0}
        aria-autocomplete="list"
        className={`${inputClass} ${!directory ? 'mono' : ''}`}
        value={query}
        placeholder={directory && !groupsDropped ? (placeholder ?? hint) : hint}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, shown.length - 1));
          else if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0));
          else if (e.key === 'Enter') {
            e.preventDefault();
            if (shown[active]) pick(shown[active]!);
            else if (manual && isId(query)) pick(manualSubject());
          } else if (e.key === 'Escape') setOpen(false);
          else return;
          e.preventDefault();
        }}
      />
      {open &&
        rect &&
        (shown.length > 0 || (manual && isId(query)) || noGroupSearch || error) &&
        createPortal(
        <ul role="listbox" style={{ position: 'fixed', top: rect.top, left: rect.left, width: Math.max(rect.width, 256) }} className="z-50 max-h-64 overflow-auto rounded-lg border border-border bg-background p-1 text-sm shadow-xl">
          {shown.map((s, i) => (
            <li
              key={`${s.type}/${s.id}`}
              role="option"
              aria-selected={i === active}
              className={`cursor-[var(--cursor-interactive)] rounded-md px-2 py-1.5 ${i === active ? 'bg-surface-secondary' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(s)}
            >
              <span className="font-medium">{s.name}</span>
              <span className="block truncate text-xs text-muted">
                {KIND_TEXT[s.type]}
                {s.detail ? ` · ${s.detail}` : ''}
              </span>
            </li>
          ))}
          {shown.length === 0 && manual && isId(query) && (
            <li role="option" aria-selected className="cursor-[var(--cursor-interactive)] rounded-md px-2 py-1.5" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(manualSubject())}>
              Use this id
              <span className="mono block truncate text-xs text-muted">{query.trim()}</span>
            </li>
          )}
          {noGroupSearch && <li className="px-2 py-1.5 text-xs text-muted">Groups cannot be searched yet: the directory is not connected for groups. An administrator grants it in Microsoft Entra.</li>}
          {error && <li className="px-2 py-1.5 text-xs text-danger">{error}</li>}
        </ul>,
        document.body,
      )}
    </div>
  );
}

/** A subject's name from the directory, with its id as the fallback; looks the id up once. */
/** " · Anna Lee" when the directory knows the person, nothing at all when it does not: a name or silence, never an id. */
export function SubjectAside({ id }: { id: string }) {
  const s = useSyncExternalStore(subscribeNames, () => subjectName(id));
  useEffect(() => {
    if (hasDirectory()) void resolveSubjects([id]);
  }, [id]);
  const known = s && s.name !== id;
  return known ? <span className="text-muted" title={id}>{` · ${s.name}`}</span> : null;
}

export function SubjectName({ id, fallback }: { id: string; fallback?: string }) {
  const s = useSyncExternalStore(subscribeNames, () => subjectName(id));
  useEffect(() => {
    if (hasDirectory()) void resolveSubjects([id]);
  }, [id]);
  const known = s && s.name !== id;
  // An id the directory was asked about and does not know is a person who has left (or a deleted group): say so, never show the id.
  const gone = !known && s?.missing;
  return (
    <span title={id}>
      {known ? s.name : gone ? (fallback ?? 'Someone no longer in the directory') : (fallback ?? id)}
      {known && s.detail ? <span className="text-muted"> · {s.detail}</span> : null}
    </span>
  );
}
